/**
 * ROLE-LENS-DESIGN-SYSTEM-V1 -- the ONE fail-closed audience decision for
 * the role-lens presentation primitives (RecordHeader / MaskedField /
 * NextActionBar / RelatedHistory / ErrorState / RoleLensBoundary).
 *
 * Pure module (no React, no I/O) so the vitest suite can pin the decision
 * table directly. Builds ON the ONE role-presentation owner
 * (frontend/src/dashboard/ui/role-presentation.ts, UNIFIED-SHELL-AND-ROLE-
 * NAVIGATION-V1): the audience vocabulary IS `PresentableRole` -- this
 * module adds the fail-closed membership decision and mints NO second role
 * union and NO second label map.
 *
 * SOURCE SCREEN (extraction): the doctor-lens route guard decision
 * `decideDoctorLensAccess` (frontend/src/dashboard/doctor/
 * doctor-patient-lens-view.ts, DOCTOR-WORKSPACE-COMPLETION-V1; proof
 * status: WALK 22 Section A PASS, data-layer boundary AUTOMATED -- the
 * rendering walk is deferred to WALK 23, so treat as IMPLEMENTATION
 * REFERENCE, UNVALIDATED for the visual layer). That guard redirects at the
 * ROUTE layer; this module generalizes only its fail-closed posture for the
 * PRESENTATION layer: an unknown, absent, or unauthorized audience NEVER
 * renders the gated content.
 *
 * CLINICAL WIRE LAW (CLAUDE.md "Clinical visibility"; docs/design/
 * role-experience/06-CRM-ROLE-VISIBILITY-DOCTRINE.md rule 4 + CLINICAL WIRE
 * FENCE): these primitives are PRESENTATION, never the security boundary.
 * Clinical fields stay structurally ABSENT from non-clinical wires at the
 * SELECT; the server fences remain the authority. A presentation allowlist
 * here can only NARROW what an already-lawful payload shows -- it can never
 * widen a read, and it must never be relied on to hide data the wire should
 * not have carried.
 */

import { PRESENTABLE_ROLES, type PresentableRole } from './role-presentation';

/** The audience vocabulary IS the presentable-role union (ONE role owner). */
export type RoleLensAudience = PresentableRole;

/**
 * Staff audiences: every presentable role except the patient. The staff
 * dashboard's shared primitives default their gated facets to this set;
 * the patient role never inherits staff facets by default (a patient-facing
 * surface must opt in explicitly, and its wire must already be lawful).
 */
export const STAFF_AUDIENCES: readonly RoleLensAudience[] =
  PRESENTABLE_ROLES.filter((role) => role !== 'patient');

/**
 * The clinical audience per the CLINICAL WIRE FENCE: the doctor is the ONLY
 * clinical audience; hierarchy alone never grants clinical detail (doctrine
 * rule 4 -- owner/gm_agm/regional_head are NOT in this set by design, and
 * the call center is NEVER clinical).
 */
export const CLINICAL_AUDIENCES: readonly RoleLensAudience[] = ['doctor'];

const KNOWN_AUDIENCES: ReadonlySet<string> = new Set<string>(PRESENTABLE_ROLES);

/** TRUE only for a recognized presentable-role token. Anything else --
 *  null, undefined, '', a foreign wire token, a typo -- is UNKNOWN and every
 *  decision below fails closed on it. */
export function isKnownAudience(value: unknown): value is RoleLensAudience {
  return typeof value === 'string' && KNOWN_AUDIENCES.has(value);
}

export type RoleLensDenialReason =
  | 'unknown_audience'
  | 'empty_audience_list'
  | 'not_in_audience';

export type RoleLensDecision =
  | { readonly kind: 'render' }
  | { readonly kind: 'deny'; readonly reason: RoleLensDenialReason };

/**
 * THE fail-closed audience decision. Deny wins every ambiguity:
 * - unknown/absent audience -> deny (unknown_audience)
 * - empty allowlist -> deny (empty_audience_list; an unconfigured gate is
 *   a closed gate, never an open one)
 * - known audience not in the allowlist -> deny (not_in_audience)
 */
export function decideRoleLens(
  audience: unknown,
  allowedAudiences: readonly RoleLensAudience[],
): RoleLensDecision {
  if (!isKnownAudience(audience)) {
    return { kind: 'deny', reason: 'unknown_audience' };
  }
  if (allowedAudiences.length === 0) {
    return { kind: 'deny', reason: 'empty_audience_list' };
  }
  if (!allowedAudiences.includes(audience)) {
    return { kind: 'deny', reason: 'not_in_audience' };
  }
  return { kind: 'render' };
}

/** Convenience form of decideRoleLens for render-or-not call sites. */
export function audienceAllows(
  audience: unknown,
  allowedAudiences: readonly RoleLensAudience[],
): boolean {
  return decideRoleLens(audience, allowedAudiences).kind === 'render';
}
